import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Shuffle, ChevronRight, PlayCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { TopicSheet } from "@/components/quiz/TopicSheet";
import { PersonalPanel } from "@/components/quiz/PersonalPanel";
import { startGeneral, startPersonal } from "@/lib/quiz";
import { useOnline } from "@/lib/offline";
import { NoXpNote, useOfflinePack } from "@/pages/OfflinePractice";
import { isLimitError, usePlan } from "@/lib/plan";

const TABS = [
  { v: "general", l: "General" },
  { v: "personal", l: "Personal" },
];

function ActiveBanner({ a, onDiscard }) {
  const navigate = useNavigate();
  return (
    <div className="card p-4 flex items-center gap-3 border-[#E50914]/50 rise" data-testid="quiz-active-banner">
      <button onClick={() => navigate(`/quiz/${a.id}`)} className="flex-1 min-w-0 flex items-center gap-3 text-left" data-testid="quiz-continue-button">
        <PlayCircle className="text-[#E50914] shrink-0" size={26} />
        <div className="min-w-0">
          <div className="font-semibold truncate">Continue {a.title}</div>
          <div className="text-xs text-[#A1A1AA]">{a.answered}/{a.total} answered</div>
        </div>
      </button>
      <button onClick={onDiscard} data-testid="quiz-discard-button" aria-label="Discard quiz" className="w-9 h-9 rounded-full flex items-center justify-center text-[#71717A] hover:text-white hover:bg-[#222225] transition-colors">
        <X size={18} />
      </button>
    </div>
  );
}

function TopicGrid({ topics, onPick, offline }) {
  const groups = [...new Set(topics.map((t) => t.group))];
  return groups.map((g) => (
    <section key={g}>
      <div className="eyebrow mb-2.5">{g}</div>
      <div className="grid grid-cols-2 gap-2.5">
        {topics.filter((t) => t.group === g).map((t) => (
          <button key={t.key} data-testid={`quiz-topic-${t.key}`} onClick={() => onPick(t)} className="card p-4 text-left min-h-[84px] flex flex-col justify-between hover:border-[#3F3F46] active:scale-[0.98] transition-[transform,border-color] duration-150">
            <span className="font-semibold leading-tight">{t.label}</span>
            <span className="text-xs text-[#71717A] mt-2">{offline ? `${offline(t.key)} offline` : t.unseen > 0 ? `${t.unseen} new` : "All seen · keep sharp"}</span>
          </button>
        ))}
      </div>
    </section>
  ));
}

export default function Quizzes() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState(() => sessionStorage.getItem("quiz_tab") || "general");
  const [topic, setTopic] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyPersonal, setBusyPersonal] = useState(false);
  const { data: plan, refetch: refetchPlan } = usePlan();
  const freeLeft = plan && plan.plan === "free" ? Math.max(0, plan.usage.quiz_questions_limit - plan.usage.quiz_questions_used) : null;
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["quiz-overview"], queryFn: () => api.get("/quizzes/overview").then((r) => r.data) });
  const online = useOnline();
  const { data: pack } = useOfflinePack();
  const offlineCount = (key) => (pack?.questions || []).filter((q) => key === "mixed" || q.category === key).length;
  const pick = (t) => (online ? setTopic(t) : offlineCount(t.key) ? navigate(`/practice/${t.key}`) : toast("No offline questions for this topic yet", { description: "Quizzes you take online are saved for offline practice." }));

  const switchTab = (v) => {
    setTab(v);
    sessionStorage.setItem("quiz_tab", v);
  };

  const go = (attempt) => {
    qc.invalidateQueries({ queryKey: ["quiz-overview"] });
    refetchPlan();
    navigate(`/quiz/${attempt.id}`);
  };

  const onStartGeneral = async (key, difficulty, length) => {
    setBusy(true);
    try {
      go(await startGeneral(key, difficulty, length));
    } catch (err) {
      if (isLimitError(err)) toast(errorMessage(err), { action: { label: "See Pro", onClick: () => navigate("/pro") } });
      else toast.error(errorMessage(err));
      setBusy(false);
      setTopic(null);
    }
  };

  const onStartPersonal = async () => {
    setBusyPersonal(true);
    try {
      go(await startPersonal());
    } catch (err) {
      toast.error(errorMessage(err));
      setBusyPersonal(false);
    }
  };

  const discard = async () => {
    await api.post(`/quizzes/attempts/${data.active.id}/abandon`).catch(() => {});
    qc.invalidateQueries({ queryKey: ["quiz-overview"] });
  };

  return (
    <div>
      <PageHeader
        eyebrow={data?.stats.completed ? `${data.stats.completed} done · ${data.stats.accuracy}% accuracy` : "Learn & remember"}
        title="Quizzes"
      />
      <div className="px-5 space-y-5">
        {!online && (
          <div className="card p-4 border-[#F59E0B]/40" data-testid="quiz-offline-card">
            <div className="font-semibold text-sm">Offline practice mode</div>
            <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">Practise {pack?.questions.length || 0} questions you've already answered online. You'll see your score and the answers, but nothing is saved and no XP is earned. Go online to earn XP.</p>
            <NoXpNote className="mt-3" />
          </div>
        )}
        {online && data?.active && <ActiveBanner a={data.active} onDiscard={discard} />}

        <div className="grid grid-cols-2 gap-1 p-1 bg-[#161618] border border-[#27272A] rounded-xl" data-testid="quiz-tabs">
          {TABS.map((t) => (
            <button key={t.v} data-testid={`quiz-tab-${t.v}`} onClick={() => switchTab(t.v)} className={`h-10 rounded-lg text-sm font-semibold transition-colors duration-150 ${tab === t.v ? "bg-[#E50914] text-white" : "text-[#A1A1AA] hover:text-white"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {isLoading && <div className="space-y-3"><div className="card h-20 animate-pulse" /><div className="card h-40 animate-pulse" /></div>}
        {isError && (
          <div className="card p-6 text-center" data-testid="quiz-overview-error">
            <p className="text-sm text-[#A1A1AA]">Couldn't load quizzes.</p>
            <button onClick={() => refetch()} className="btn-secondary mt-4">Try again</button>
          </div>
        )}

        {data && tab === "personal" && !online && <p className="text-xs text-[#A1A1AA]" data-testid="quiz-personal-offline">Personal quizzes need internet.</p>}
        {data && tab === "general" && (
          <div className="space-y-6 pb-4">
            {online && freeLeft !== null && (
              <div className="flex items-center justify-between gap-3 text-xs" data-testid="quiz-free-usage">
                <span className={freeLeft ? "text-[#A1A1AA]" : "text-[#FF6B6B]"}>{freeLeft ? `${freeLeft} of ${plan.usage.quiz_questions_limit} free questions left today` : "You've used today's free questions"}</span>
                <Link to="/pro" className="font-semibold text-white underline underline-offset-2" data-testid="quiz-free-upgrade">Unlimited with Pro</Link>
              </div>
            )}
            <button data-testid="quiz-topic-mixed" onClick={() => pick({ key: "mixed", label: "Mixed topics", total: data.total_questions, unseen: data.total_unseen, by_difficulty: null })} className="card w-full p-5 flex items-center gap-4 text-left hover:border-[#3F3F46] active:scale-[0.99] transition-[transform,border-color] duration-150">
              <div className="w-11 h-11 rounded-xl bg-[#E50914] flex items-center justify-center shrink-0"><Shuffle size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Mixed topics</div>
                <div className="text-xs text-[#71717A]">{online ? `${data.total_questions} questions across every position` : `${offlineCount("mixed")} available offline`}</div>
              </div>
              <ChevronRight size={18} className="text-[#52525B]" />
            </button>
            <TopicGrid topics={data.topics} onPick={pick} offline={!online && offlineCount} />
          </div>
        )}

        {data && online && tab === "personal" && <PersonalPanel personal={data.personal} onStart={onStartPersonal} busy={busyPersonal} />}
      </div>
      <TopicSheet topic={topic} onClose={() => setTopic(null)} onStart={onStartGeneral} busy={busy} />
    </div>
  );
}
